import React, { useEffect } from 'react';
import { Outlet, Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useOrganization } from '../../hooks/useOrganization';

/**
 * OrgLayout - Layout wrapper for org-scoped routes
 *
 * Validates that:
 * 1. User is authenticated
 * 2. User has access to the organization in the URL
 * 3. Sets the current organization context based on URL slug
 */
const OrgLayout = () => {
  const { orgSlug } = useParams();
  const { organizations, hasOrganizations, loading: authLoading } = useAuth();
  const { setCurrentOrg, hasAccessToOrg, getOrgBySlug, isLoading: orgLoading } = useOrganization();

  // Set current org based on URL slug
  useEffect(() => {
    if (!orgSlug || authLoading || orgLoading) return;

    const org = getOrgBySlug(orgSlug);
    if (org) {
      setCurrentOrg(org);
    }
  }, [orgSlug, authLoading, orgLoading, getOrgBySlug, setCurrentOrg]);

  // Show nothing while loading
  if (authLoading || orgLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // User has no organizations
  if (!hasOrganizations) {
    return <Navigate to="/waiting" replace />;
  }

  // Validate user has access to this organization
  if (!hasAccessToOrg(orgSlug)) {
    // User doesn't have access to this org - redirect to org picker or their default org
    return <Navigate to="/organizations" replace />;
  }

  // Render child routes
  return <Outlet />;
};

export default OrgLayout;
